import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * ADM -> Review queue, calea noua. Perechile vechi, din
 * `application/controllers/Adm.php`:
 *
 *   pending            -> GET  /adm/pending
 *   pendingData        -> GET  /adm/pending/table
 *   promote            -> POST /adm/pending/{id}/promote
 *   mergeInto          -> POST /adm/pending/{id}/merge
 *   ignoreDiscovered   -> POST /adm/pending/{id}/ignore
 *   savePolicy         -> POST /adm/pending/policy
 *   ciSearch           -> GET  /adm/pending/ci-search
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * COLOANELE VIN DE PE SERVER, ca lista de chei. Doua din ele - fabricantul si
 * amprenta - exista numai cand exista dovada, deci indicii de dupa ele se muta;
 * socotite si aici, si acolo, cele doua ar fi ajuns sa nu fie de acord, si
 * atunci tabelul se ordoneaza dupa o coloana pe care n-a apasat nimeni. De-aia
 * si ordonarea se cere pe NUMELE coloanei, nu pe indice.
 */

/** Cheile coloanelor, in ordinea in care le trimite serverul. */
export type PendingColumn =
  | 'name'
  | 'ip'
  | 'mac'
  | 'manufacturer'
  | 'os'
  | 'fingerprint'
  | 'company'
  | 'match'
  | 'first_seen'
  | 'decide'

export interface PendingShell {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: { days: number; company_id: number; relay_device_id: number; run_type: number; state: number }
  states: { value: string; label: string }[]
  waiting: number
  policy: { auto_merge: boolean; auto_promote: boolean }
  canWrite: boolean
  columns: PendingColumn[]
  /** Clasele de aparat active ale furnizorului - alegatorul din "Add to CMDB". */
  classes: CiOption[]
}

export interface PendingRow {
  ID: number
  DT_RowId: string
  state: number
  state_label: string
  /** Utilizatorul 0 e platforma: randul l-au asezat regulile, nu un om. */
  decided_by_user_id: number
  company_id: number
  company_name: string
  match_score: number
  matched_ci_id: number
  matched_name: string
  discovered_device_id: number
  device_name: string
  /** Numele intreg (wks02.cecdemo.local), cand e altul decat numele scurt; gol altfel. */
  fqdn?: string
  ip: string
  mac: string
  manufacturer_name: string
  os: string
  first_seen: string
  /** Concluzia scanerului, in cuvinte - plafonata pe server. */
  hints: string[]
  hints_more: number
  /** Socoteala din spatele ei. */
  ports: string[]
  ports_more: number
  /** Tipul trimis de agent; 0 = n-a trimis (agentii Go), si clasa se ghiceste. */
  device_type: number
  /** Felul ghicit pe server (`AutoClassifier`) cand tipul e 0; '' = nu se stie. */
  kind: string
  /** Clasa cu care ar intra randul (a tipului sau a felului ghicit); 0 = niciuna. */
  class_id: number
  /** De unde vine clasa: 'type', 'guess', sau '' pe un rand deja hotarat. */
  class_from: '' | 'type' | 'guess'
}

export interface PendingTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: PendingRow[]
}

export interface CiOption {
  id: number
  name: string
}

export function pendingShellQuery() {
  return queryOptions({
    queryKey: ['adm', 'pending', 'shell'],
    queryFn: () => apiGet<PendingShell>('/adm/pending'),
  })
}

export interface PendingTableRequest {
  draw: number
  start: number
  length: number
  /** Cheia coloanei, nu indicele - vezi capul fisierului. */
  orderColumn: string
  orderDir: 'asc' | 'desc'
  search: string
  state: string
}

export function pendingTableQuery(r: PendingTableRequest) {
  const params = new URLSearchParams({
    draw: String(r.draw),
    start: String(r.start),
    length: String(r.length),
    orderColumn: r.orderColumn,
    orderDir: r.orderDir,
    search: r.search,
    state: r.state,
  })

  return queryOptions({
    queryKey: ['adm', 'pending', 'table', r],
    queryFn: () => apiGet<PendingTablePage>(`/adm/pending/table?${params.toString()}`),
  })
}

/** Elementele dintre care se alege la imbinare - ingustate pe firma, pe server. */
export function ciSearchQuery(term: string) {
  return queryOptions({
    queryKey: ['adm', 'pending', 'ci-search', term],
    queryFn: () => apiGet<CiOption[]>(`/adm/pending/ci-search?q=${encodeURIComponent(term)}`),
    enabled: term.trim().length >= 2,
  })
}

/**
 * `classId` = clasa aleasa de om pentru un aparat fara tip; fara ea, o alege
 * serverul. Cand nici serverul n-o gaseste, raspunde 400 cu
 * `reason: 'needs_class'` si lista claselor (`PromoteNeedsClass`, in
 * `ApiError.details`).
 */
export function promotePending(id: number, classId = 0) {
  return apiPost<{ status: string; ci_id: number; message: string }>(
    `/adm/pending/${id}/promote`,
    classId > 0 ? { class_id: classId } : {},
  )
}

/** Corpul raspunsului cand serverul cere omului sa aleaga clasa. */
export interface PromoteNeedsClass {
  error: 'invalid'
  reason: 'needs_class'
  kind: string
  class_id: number
  classes: CiOption[]
  message: string
}

export function needsClass(details: Record<string, unknown> | undefined): PromoteNeedsClass | null {
  return details && details.reason === 'needs_class' && Array.isArray(details.classes)
    ? (details as unknown as PromoteNeedsClass)
    : null
}

export function mergePending(id: number, ciId: number) {
  return apiPost<{ status: string; ci_id: number; message: string }>(`/adm/pending/${id}/merge`, { ci_id: ciId })
}

export function ignorePending(id: number) {
  return apiPost<{ status: string; message: string }>(`/adm/pending/${id}/ignore`, {})
}

export function savePendingPolicy(autoMerge: boolean, autoPromote: boolean) {
  return apiPost<{ status: string; message: string }>('/adm/pending/policy', {
    auto_merge: autoMerge ? 1 : 0,
    auto_promote: autoPromote ? 1 : 0,
  })
}
